const sanitizeGenres = (genres) => {
  if (!Array.isArray(genres)) {
    return [];
  }

  return genres.map(({ Id, Name }) => ({
    id: Id,
    name: Name,
  }));
};

const sanitizeAssessment = (assessment) => {
  if (!assessment) {
    return null;
  }

  return {
    id: assessment.Id,
    name: assessment.Name,
    image: assessment.Image,
    url: assessment.Url,
  };
};

const sanitizeToolTips = (tooltips) => {
  if (!Array.isArray(tooltips)) {
    return [];
  }

  return tooltips.map((tooltip) => ({
    id: tooltip.Id,
    iconClass: tooltip.IconClass,
    text: tooltip.Text,
  }));
};

const sanitizeFilmAttributes = (attributes) => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes.map((attribute) => ({
    id: attribute.Id,
    name: attribute.Name,
    image: attribute.Image,
    title: attribute.Title,
    titleShort: attribute.TitleShort,
    description: attribute.Description,
    isHidden: attribute.IsHidden,
    isPreFilmTitle: attribute.IsPreFilmTitle,
  }));
};

const sanitizeAuditoriumAttributes = (attributes) => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes.map((attribute) => ({
    id: attribute.Id,
    name: attribute.Name,
    icon: attribute.Icon,
    title: attribute.Title,
    description: attribute.Description,
  }));
};

const sanitizeAuditorium = (auditorium) => ({
  oid: auditorium.Oid,
  name: auditorium.Name,
  attributes: sanitizeAuditoriumAttributes(auditorium.Attributes),
});

const sanitizePerformanceAttributes = (attributes) => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes.map((attr) => ({
    id: attr.Id,
    name: attr.Name,
    image: attr.Image,
    title: attr.Title,
    description: attr.Description,
  }));
};

const sanitizeFilmSerie = (filmSerie) => {
  if (!filmSerie) {
    return null;
  }

  return {
    id: filmSerie.Id,
    name: filmSerie.Name,
    showInEvents: !filmSerie.IsNotShowOnEvent,
    showInSchedule: !filmSerie.IsNotShowOnSchedule,
  };
};

const sanitizePerformances = (performances) => {
  if (!Array.isArray(performances)) {
    return [];
  }

  return performances.map((perf) => ({
    id: perf.Id,
    siteId: perf.SiteId,
    siteName: perf.SiteName,
    siteShortName: perf.SiteShortName,
    releaseTypeId: perf.ReleaseTypeId,
    releaseType: perf.ReleaseType,
    actualDate: perf.ActualDate,
    showTime: perf.ShowTime,
    filmSerie: sanitizeFilmSerie(perf.FilmSerie),
    attributes: sanitizePerformanceAttributes(perf.Attributes),
    isSellable: perf.IsSellable,
    auditorium: sanitizeAuditorium(perf.Auditorium),
  }));
};

const sanitizeDeal = (deal) => {
  if (!deal) {
    return null;
  }

  return {
    price: deal.Price,
    deal: deal.Deal,
    combo: deal.Combo,
    url: deal.Url,
  };
};

const sanitizeVod = (vod) => {
  if (!vod) {
    return null;
  }

  return {
    id: vod.Id,
    title: vod.Title,
    rated: vod.MinAge,
    productionYear: vod.Year,
    runtime: vod.Duration,
    cover: vod.Cover,
    rentPrice: vod.RentPrice,
    rentStart: vod.RentStart,
    rentEnd: vod.RentEnd,
    buyPrice: vod.BuyPrice,
    buyStart: vod.BuyStart,
    buyEnd: vod.BuyEnd,
    link: vod.Link,
    // dummy performance so the scheduleFilter doesnt need any extra checks
    performances: [{}],
  };
};

const sanitizeVods = (vods) => {
  if (!Array.isArray(vods)) {
    return [];
  }

  return vods.map((vod) => sanitizeVod(vod));
}

const sanitizeFilm = (film) => ({
  id: film.Id,
  title: film.Titel,
  originalTitle: film.OriginalTitel,
  description: film.Beschreibung,
  direction: film.Regie,
  actors: film.Darsteller,
  runtime: film.Laufzeit,
  productionCountry: film.Land,
  productionYear: film.Jahr,
  genre: film.Genre,
  genres: sanitizeGenres(film.Genres),
  releaseDate: film.Filmstart,
  website: film.Url,
  hasTrailer: film.HasTrailer,
  rated: film.Fsk,
  teaser: film.Teaser,
  recommendation: film.Recommendation,
  week: film.Week,
  weekDisabled: film.WeekDisabled,
  topTenPosition: film.TopTenPosition,
  assessment: sanitizeAssessment(film.Assessment),
  deal: sanitizeDeal(film.Deal),
  vod: sanitizeVod(film.Vod),
  tooltips: sanitizeToolTips(film.ToolTips),
  attributes: sanitizeFilmAttributes(film.Attributes),
  performances: sanitizePerformances(film.Performances),
});

const sanitizeFilms = (films) => {
  if (!Array.isArray(films)) {
    return [];
  }

  return films.map((film) => sanitizeFilm(film));
};

const getFilterFromValue = (value) => {
  try {
    return JSON.parse(atob(value));
  } catch (error) {
    console.log('invalid filter!');
    return null;
  }
};

const sanitizeScheduleFilters = (filters) => {
  if (!Array.isArray(filters)) {
    return [];
  }

  return filters.map((filter) => ({
    id: filter.Id,
    label: filter.Label,
    scope: filter.Category,
    value: filter.Value,
    filter: getFilterFromValue(filter.Value),
    sortOrder: filter.SortOrder,
    isTopFilter: filter.IsTopFilter || false,
    isVodFilter: filter.IsVodFilter || false,
    isSelected: filter.IsSelected || false,
  }));
};

const sanitizeScheduleSettings = (settings) => ({
  separateSites: settings.SeparateSites,
  colorTicketButton: settings.ColorTicketButton,
  performanceBlock: settings.PerformanceBlock,
});

export const mapSchedule = ({ Films, Filters, Settings }) => ({
  films: sanitizeFilms(Films),
  filters: sanitizeScheduleFilters(Filters),
  settings: sanitizeScheduleSettings(Settings),
});

export const mapVods = (vods) => sanitizeVods(vods);
